import { useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import axios from 'axios';
import { useTranslation } from 'react-i18next';
import { toast } from 'sonner';
import { ArrowLeft, Pencil, Trash2, Download, Eye, Star, AlertTriangle, Copy, MessageCircle, Mail, Link2, RotateCcw, Share2 } from 'lucide-react';

import { QuoteLayout } from '@/components/quotes/QuoteLayout';
import { useAuth } from '@/contexts/AuthContext';
import { hasQuotePermission } from '@/lib/quotePermissions';
import { Button } from '@/components/ui/button';
import { formatMinor } from '@/lib/quoteMoney';
import { QuoteStatusBadge, STATUS_NEXT } from './quoteStatus';
import { QuoteMaterialsCard } from './QuoteMaterialsCard';
import { QuoteDevicesCard } from './QuoteDevicesCard';

const API_URL = process.env.REACT_APP_API_URL;

const isMissingPrice = (item) => item?.price_unset === true
  || item?.unit_price_minor == null
  || Number(item?.unit_price_minor) <= 0;

async function getPdfErrorDetail(err) {
  let payload = err?.response?.data;
  if (payload instanceof Blob) {
    try { payload = JSON.parse(await payload.text()); } catch { return null; }
  }
  return payload?.detail || null;
}

function PackageBlock({ pkg, currency, taxLabel, t, showCosts = true }) {
  const tot = pkg.totals || {};
  const items = pkg.items || [];
  return (
    <div className={`bg-white rounded-2xl border p-6 mb-4 ${pkg.is_recommended ? 'border-[#2563EB] ring-2 ring-blue-100' : 'border-[#E5E5E5]'}`}
         data-testid={`detail-pkg-${pkg.key}`}>
      <div className="flex items-center justify-between mb-4">
        <div className="flex items-center gap-2">
          {pkg.is_recommended && <Star className="w-4 h-4 fill-[#2563EB] text-[#2563EB]" />}
          <h3 className="text-lg font-bold text-[#1A1A2E]">{pkg.name}</h3>
          {pkg.is_recommended && (
            <span className="text-[10px] uppercase tracking-wider bg-[#2563EB] text-white px-2 py-0.5 rounded-full">{t('detail.recommended')}</span>
          )}
        </div>
        <p className="text-xl font-bold text-[#2563EB]">{formatMinor(tot.grand_total_minor || 0, currency)}</p>
      </div>
      {items.length > 0 && (
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead className="bg-[#F8FAFC] text-[#475569]">
              <tr>
                <th className="text-left px-3 py-2">{t('detail.colName')}</th>
                <th className="text-right px-3 py-2">{t('detail.colQuantity')}</th>
                <th className="text-right px-3 py-2">{t('detail.colUnit')}</th>
                <th className="text-right px-3 py-2">{t('detail.colDiscount')}</th>
                <th className="text-right px-3 py-2">{t('detail.colTotal')}</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#F1F5F9]">
              {items.map((it, i) => {
                const missing = isMissingPrice(it);
                return (
                <tr key={i} className={missing ? 'bg-amber-50/60' : ''}>
                  <td className="px-3 py-2 text-[#1A1A2E]">{it.name}{it.unit && ` (${it.unit})`}</td>
                  <td className="px-3 py-2 text-right">{it.quantity}</td>
                  <td className={`px-3 py-2 text-right ${missing ? 'font-medium text-amber-700' : ''}`}>
                    {missing ? t('detail.priceMissing') : formatMinor(it.unit_price_minor, currency)}
                  </td>
                  <td className="px-3 py-2 text-right text-[#94A3B8]">%{Number(it.line_discount_percent || 0).toFixed(2)}</td>
                  <td className={`px-3 py-2 text-right font-medium ${missing ? 'text-amber-700' : ''}`}>
                    {missing ? t('detail.pricePending') : formatMinor(it.line_total_minor, currency)}
                  </td>
                </tr>
              );})}
            </tbody>
          </table>
        </div>
      )}
      <div className="mt-4 grid grid-cols-2 sm:grid-cols-4 gap-3 text-sm border-t border-[#F1F5F9] pt-3">
        <Cell label={t('detail.rowSubtotal')} value={formatMinor(tot.subtotal_minor || 0, currency)} />
        {(tot.line_discount_minor || 0) > 0 && <Cell label={t('detail.rowLineDiscount')} value={`-${formatMinor(tot.line_discount_minor, currency)}`} />}
        {(tot.global_discount_minor || 0) > 0 && <Cell label={t('detail.rowGlobalDiscount')} value={`-${formatMinor(tot.global_discount_minor, currency)}`} />}
        {(tot.tax_minor || 0) > 0 && <Cell label={taxLabel || t('detail.rowTax')} value={formatMinor(tot.tax_minor, currency)} />}
        <Cell label={t('detail.rowGrandTotal')} value={formatMinor(tot.grand_total_minor || 0, currency)} bold />
      </div>
      {/* Cost + profit — panel-only, never in PDF */}
      {showCosts && <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 gap-3 text-xs border-t border-dashed border-emerald-200 pt-3 bg-emerald-50/40 -mx-6 -mb-6 px-6 pb-3 rounded-b-2xl">
        <Cell label={t('detail.rowTotalCost')} value={formatMinor(tot.total_cost_minor || 0, currency)} muted />
        <Cell label={t('detail.rowGrossProfit')} value={formatMinor(tot.gross_profit_minor || 0, currency)} muted highlight />
        <Cell label={t('detail.rowProfitMargin')} value={`%${Number(tot.profit_margin_percent || 0).toFixed(2)}`} muted highlight />
      </div>}
    </div>
  );
}

function Cell({ label, value, bold, muted, highlight }) {
  return (
    <div>
      <p className={`text-xs ${muted ? 'text-emerald-800' : 'text-[#94A3B8]'}`}>{label}</p>
      <p className={`${bold ? 'font-bold text-[#2563EB] text-base' : ''} ${highlight ? 'font-semibold text-emerald-700' : 'text-[#1A1A2E]'}`}>
        {value}
      </p>
    </div>
  );
}

export function QuoteDetail() {
  const { t, i18n } = useTranslation('quotes');
  const { user } = useAuth();
  const showCosts = hasQuotePermission(user, 'costs');
  const lang = (i18n.resolvedLanguage || i18n.language || 'tr').split('-')[0];
  const { id } = useParams();
  const navigate = useNavigate();
  const [quote, setQuote] = useState(null);
  const [loading, setLoading] = useState(true);
  const [downloading, setDownloading] = useState(false);
  const [previewing, setPreviewing] = useState(false);
  const [copying, setCopying] = useState(false);
  const [changingStatus, setChangingStatus] = useState(false);
  const [publicLink, setPublicLink] = useState(null);
  const [sharing, setSharing] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const [quoteRes, linkRes] = await Promise.all([
          axios.get(`${API_URL}/api/quote/quotes/${id}`),
          axios.get(`${API_URL}/api/quote/quotes/${id}/public-link`).catch(() => null),
        ]);
        setQuote(quoteRes.data);
        setPublicLink(linkRes?.data || null);
      } catch (err) {
        toast.error(err.response?.data?.detail || t('common.error'));
        navigate('/quotes');
      } finally { setLoading(false); }
    })();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const remove = async () => {
    if (!window.confirm(t('form.deleteConfirm'))) return;
    try {
      await axios.delete(`${API_URL}/api/quote/quotes/${id}`);
      toast.success(t('form.deletedToast'));
      navigate('/quotes');
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const copy = async () => {
    setCopying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/quotes/${id}/copy`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || t('common.error'));
    } finally { setCopying(false); }
  };

  const createRevision = async () => {
    setCopying(true);
    try {
      const { data } = await axios.post(`${API_URL}/api/quote/quotes/${id}/revision`);
      navigate(`/quotes/${data.id}/edit`);
    } catch (err) {
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || t('common.error'));
    } finally { setCopying(false); }
  };

  const ensureLink = async () => {
    if (publicLink?.url) return publicLink;
    const { data } = await axios.post(`${API_URL}/api/quote/quotes/${id}/public-link`);
    setPublicLink(data);
    // Link creation only changes share/status metadata. Avoid a second full
    // quote fetch; update the visible status locally instead.
    setQuote((current) => current ? {
      ...current,
      public_link_active: true,
      status: current.status === 'draft' ? 'sent' : current.status,
    } : current);
    return data;
  };

  const share = async (kind) => {
    if (sharing) return;
    setSharing(true);
    // Open WhatsApp on the original click; browser popup blockers can reject
    // windows opened after the link request completes.
    const popup = kind === 'whatsapp' ? window.open('', '_blank') : null;
    try {
      const linkInfo = await ensureLink();
      const url = linkInfo.url;
      const name = (quote.customer_snapshot || {}).name || '';
      const issuerName = (linkInfo.issuer_name || '').trim();
      const quoteNo = quote.quote_number || '';
      const message = (quote.quote_language === 'en'
        ? [
            `Hello ${name},`,
            '',
            issuerName
              ? `Your quote prepared by ${issuerName} is ready for review:`
              : 'Your quote is ready for review:',
            url,
            quoteNo ? `Quote No: ${quoteNo}` : '',
            'Sent via PaydaAI.',
          ].filter(Boolean).join('\n')
        : [
            `Merhaba ${name},`,
            '',
            issuerName
              ? `${issuerName} tarafından hazırlanan fiyat teklifinizi aşağıdaki bağlantıdan inceleyebilirsiniz:`
              : 'Fiyat teklifinizi aşağıdaki bağlantıdan inceleyebilirsiniz:',
            url,
            quoteNo ? `Teklif No: ${quoteNo}` : '',
            'PaydaAI üzerinden gönderilmiştir.',
          ].filter(Boolean).join('\n'));
      if (kind === 'copy') {
        await navigator.clipboard.writeText(url);
        toast.success(t('detail.linkCopied'));
      } else if (kind === 'whatsapp') {
        const phone = (quote.customer_snapshot || {}).phone || '';
        const cc = (quote.customer_snapshot || {}).country_code || 'TR';
        const codes = { TR: '90', US: '1', CA: '1', GB: '44', DE: '49', FR: '33', NL: '31', BE: '32', AT: '43', CH: '41', SE: '46', NO: '47', DK: '45', PL: '48' };
        const digits = phone.replace(/\D/g, '');
        const international = phone.trim().startsWith('+') || phone.trim().startsWith('00')
          ? (phone.trim().startsWith('00') ? digits.slice(2) : digits)
          : (codes[cc] && digits ? (digits.startsWith(codes[cc]) && digits.length > 9 ? digits : codes[cc] + digits.replace(/^0+/, '')) : '');
        const target = `https://wa.me/${international}?text=${encodeURIComponent(message)}`;
        if (popup) popup.location.href = target;
        else window.location.href = target;
      } else if (kind === 'email') {
        const subject = t('detail.shareSubject', { number: quote.quote_number });
        const address = (quote.customer_snapshot || {}).email || '';
        // PaydaAI does not send this mail. Prepare it for the user's own mail
        // account. Copy the body first so desktop systems without a default
        // mail handler still leave the user with a usable message.
        try { await navigator.clipboard.writeText(message); } catch (_) {}
        window.location.href = `mailto:${encodeURIComponent(address)}?subject=${encodeURIComponent(subject)}&body=${encodeURIComponent(message)}`;
        toast.success(lang === 'tr'
          ? 'E-posta taslağı açılıyor. Açılmazsa teklif mesajı panoya kopyalandı.'
          : 'Opening your email draft. If no mail app opens, the message was copied.');
      }
    } catch (err) {
      if (popup) popup.close();
      toast.error(err.response?.data?.detail?.message || err.response?.data?.detail || t('common.error'));
    } finally { setSharing(false); }
  };

  const revokeLink = async () => {
    if (!window.confirm(t('detail.revokeConfirm'))) return;
    try {
      const { data } = await axios.delete(`${API_URL}/api/quote/quotes/${id}/public-link`);
      setPublicLink(data);
      toast.success(t('detail.linkRevoked'));
    } catch (err) { toast.error(err.response?.data?.detail || t('common.error')); }
  };

  const sharePdf = async () => {
    try {
      const res = await axios.get(`${API_URL}/api/quote/quotes/${id}/pdf`, { responseType: 'blob' });
      const file = new File([res.data], `quote-${quote.quote_number}.pdf`, { type: 'application/pdf' });
      if (navigator.canShare?.({ files: [file] })) {
        await navigator.share({ files: [file], title: quote.title });
      } else {
        await downloadPdf();
      }
    } catch (err) {
      if (err.name !== 'AbortError') await showPdfError(err);
    }
  };

  const changeStatus = async (status) => {
    if (!status) return;
    setChangingStatus(true);
    try {
      const { data } = await axios.patch(`${API_URL}/api/quote/quotes/${id}/status`, { status });
      setQuote(data);
    } catch (err) {
      toast.error(err.response?.data?.detail || t('common.error'));
    } finally { setChangingStatus(false); }
  };

  const showPdfError = async (err) => {
    const detail = await getPdfErrorDetail(err);
    if (detail?.reason === 'missing_prices') {
      const names = (detail.missing_prices || []).map((row) => row?.name).filter(Boolean);
      const suffix = names.length ? `: ${names.slice(0, 5).join(', ')}${names.length > 5 ? '…' : ''}` : '';
      toast.error(`${detail.message || t('detail.missingPricePdfError')}${suffix}`);
    } else {
      toast.error(typeof detail === 'string' ? detail : (detail?.message || t('detail.pdfError')));
    }
  };

  const previewPdf = async () => {
    setPreviewing(true);
    // Open synchronously from the click so browser popup blockers do not
    // reject the preview while the PDF request is in flight.
    const previewWindow = window.open('', '_blank');
    try {
      const res = await axios.get(`${API_URL}/api/quote/quotes/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      if (previewWindow) {
        previewWindow.location.href = url;
        setTimeout(() => URL.revokeObjectURL(url), 60_000);
      } else {
        URL.revokeObjectURL(url);
        toast.error(t('detail.previewBlocked'));
      }
    } catch (err) {
      if (previewWindow) previewWindow.close();
      await showPdfError(err);
    } finally { setPreviewing(false); }
  };

  const downloadPdf = async () => {
    setDownloading(true);
    try {
      const res = await axios.get(`${API_URL}/api/quote/quotes/${id}/pdf`, { responseType: 'blob' });
      const blob = new Blob([res.data], { type: 'application/pdf' });
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `teklif_${quote.quote_number}.pdf`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (err) {
      await showPdfError(err);
    } finally { setDownloading(false); }
  };

  if (loading || !quote) {
    return <QuoteLayout><div className="p-8 text-center text-[#595959]">{t('common.loading')}</div></QuoteLayout>;
  }

  const c = quote.customer_snapshot || {};
  const packages = quote.packages || [];
  const missingPriceCount = packages.reduce(
    (count, pkg) => count + (pkg.items || []).filter(isMissingPrice).length,
    0,
  );
  const ordered = [...packages].sort((a, b) => (b.is_recommended ? 1 : 0) - (a.is_recommended ? 1 : 0));
  const locale = {
    tr: 'tr-TR', en: 'en-US', de: 'de-DE', fr: 'fr-FR', es: 'es-ES',
  }[lang] || 'en-US';
  const nextStatuses = STATUS_NEXT[quote.status || 'draft'] || [];

  return (
    <QuoteLayout>
      <div className="max-w-4xl mx-auto" data-testid="quote-detail-page">
        <button onClick={() => navigate('/quotes')} className="text-sm text-[#2563EB] hover:underline flex items-center gap-1 mb-4">
          <ArrowLeft className="w-4 h-4" /> {t('detail.backToList')}
        </button>

        <div className="flex flex-wrap items-start justify-between gap-4 mb-6">
          <div>
            <p className="text-sm text-[#94A3B8]">{t('detail.quoteNumber')}</p>
            <h1 className="text-2xl font-bold text-[#1A1A2E]" data-testid="detail-quote-number">{quote.quote_number}</h1>
            <p className="text-[#475569] mt-1">{quote.title}</p>
          </div>
          <div className="grid w-full grid-cols-2 gap-1.5 sm:flex sm:w-auto sm:gap-2" data-testid="detail-actions">
            <Button
              onClick={previewPdf}
              disabled={previewing}
              variant="outline"
              className="h-9 min-w-0 px-2 text-xs sm:h-10 sm:px-4 sm:text-sm"
              data-testid="detail-pdf-preview-btn"
            >
              <Eye className="mr-1 h-4 w-4 shrink-0 sm:mr-2" />
              <span className="truncate">{previewing ? t('detail.preparingPdf') : (lang === 'en' ? 'Preview' : 'Önizle')}</span>
            </Button>
            <Button
              onClick={downloadPdf}
              disabled={downloading}
              className="h-9 min-w-0 px-2 text-xs bg-emerald-600 hover:bg-emerald-700 sm:h-10 sm:px-4 sm:text-sm"
              data-testid="detail-pdf-btn"
            >
              <Download className="mr-1 h-4 w-4 shrink-0 sm:mr-2" /> <span className="truncate">{downloading ? t('detail.preparingPdf') : t('detail.downloadPdf')}</span>
            </Button>
            <Button variant="outline" onClick={copy} disabled={copying} className="h-9 px-2 text-xs sm:h-10 sm:px-4 sm:text-sm" data-testid="detail-copy-btn">
              <Copy className="mr-1 h-4 w-4 shrink-0 sm:mr-2" /> {t('list.copyQuote')}
            </Button>
            {quote.status === 'draft' && !quote.public_link_active && (
              <Button onClick={() => navigate(`/quotes/${quote.id}/edit`)} className="h-9 min-w-0 px-2 text-xs bg-[#2563EB] hover:bg-[#1E4ED8] sm:h-10 sm:px-4 sm:text-sm">
                <Pencil className="mr-1 h-4 w-4 shrink-0 sm:mr-2" /> <span className="truncate">{t('detail.editDraft')}</span>
              </Button>
            )}
            {(quote.status !== 'draft' || quote.public_link_active) && (
              <Button variant="outline" onClick={createRevision} disabled={copying} className="h-9 px-2 text-xs sm:h-10 sm:px-4 sm:text-sm">
                <RotateCcw className="mr-1 h-4 w-4" /> {t('detail.newRevision')}
              </Button>
            )}
            <Button variant="outline" onClick={remove} className="h-9 px-2 text-xs text-red-600 border-red-200 hover:bg-red-50 sm:h-10 sm:px-4 sm:text-sm">
              <Trash2 className="mr-1 h-4 w-4 shrink-0 sm:mr-2" /> <span>{t('detail.delete')}</span>
            </Button>
          </div>
        </div>

        <div className="mb-6 rounded-2xl border border-[#E5E5E5] bg-white p-4">
          <h2 className="mb-3 font-semibold">{t('detail.onlineSharing')}</h2>
          <div className="flex flex-wrap gap-2">
            <Button variant="outline" disabled={sharing} onClick={() => share('whatsapp')}><MessageCircle className="mr-2 h-4 w-4" />{t('detail.whatsApp')}</Button>
            <Button variant="outline" disabled={sharing} onClick={() => share('email')}><Mail className="mr-2 h-4 w-4" />{t('detail.emailShare')}</Button>
            <Button variant="outline" disabled={sharing} onClick={() => share('copy')}><Link2 className="mr-2 h-4 w-4" />{t('detail.copyLink')}</Button>
            <Button variant="outline" onClick={sharePdf}><Share2 className="mr-2 h-4 w-4" />{t('detail.pdfShare')}</Button>
            {publicLink?.url && <Button variant="outline" onClick={revokeLink} className="text-red-600">{t('detail.revokeLink')}</Button>}
          </div>
          {publicLink?.url && <p className="mt-3 break-all text-xs text-slate-500">{publicLink.url}</p>}
          <div className="mt-4 grid gap-2 text-xs text-slate-600 sm:grid-cols-3">
            <p>{t('detail.firstViewed')}: {quote.first_viewed_at ? new Date(quote.first_viewed_at).toLocaleString(locale) : '—'}</p>
            <p>{t('detail.lastViewed')}: {quote.last_viewed_at ? new Date(quote.last_viewed_at).toLocaleString(locale) : '—'}</p>
            <p>{t('detail.views')}: {quote.view_count || 0}</p>
          </div>
          {quote.customer_response?.comment && <p className="mt-3 rounded-lg bg-amber-50 p-3 text-sm text-amber-900">{t('detail.customerMessage')}: {quote.customer_response.comment}</p>}
          {quote.accepted_package_key && (quote.packages || []).length > 1 && (
            <p className="mt-3 rounded-lg bg-emerald-50 p-3 text-sm text-emerald-900" data-testid="detail-accepted-package">
              {t('detail.acceptedPackage')}: <strong>{(quote.packages.find((p) => p.key === quote.accepted_package_key) || {}).name || quote.accepted_package_key}</strong>
            </p>
          )}
        </div>

        <div className="mb-6 rounded-2xl border border-[#E5E5E5] bg-white p-4">
          <div className="flex flex-wrap items-center gap-3">
            <span className="text-sm font-medium">{t('detail.status')}:</span>
            <QuoteStatusBadge status={quote.status} t={t} />
            {nextStatuses.length > 0 && (
              <select value="" disabled={changingStatus}
                onChange={(e) => changeStatus(e.target.value)}
                aria-label={t('detail.changeStatus')}
                className="ml-auto rounded-md border border-slate-200 bg-white px-3 py-2 text-sm"
                data-testid="detail-status-select">
                <option value="" disabled>{t('detail.changeStatus')}</option>
                {nextStatuses.map((status) => (
                  <option key={status} value={status}>{t(`list.status.${status}`)}</option>
                ))}
              </select>
            )}
          </div>
          <div className="mt-3 border-t border-slate-100 pt-3">
            <p className="mb-2 text-sm font-semibold">{t('detail.statusHistory')}</p>
            {(quote.status_history || []).map((event, index) => (
              <p key={index} className="text-sm text-slate-600">
                {t(`list.status.${event.status}`, { defaultValue: event.status })}: {event.at ? new Date(event.at).toLocaleString(locale) : '—'}
              </p>
            ))}
          </div>
        </div>

        {quote.status === 'accepted' && <QuoteMaterialsCard quote={quote} />}
        {quote.status === 'accepted' && hasQuotePermission(user, 'service') && <QuoteDevicesCard quote={quote} />}

        <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-6">
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4">
            <p className="text-xs text-[#94A3B8]">{t('detail.customer')}</p>
            <p className="font-semibold text-[#1A1A2E]">{c.name || '—'}</p>
            {c.email && <p className="text-sm text-[#595959]">{c.email}</p>}
            {c.phone && <p className="text-sm text-[#595959]">{c.phone}</p>}
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4">
            <p className="text-xs text-[#94A3B8]">{t('detail.pricing')}</p>
            <p className="text-sm">{t('detail.taxLabel')}: <b>%{Number(quote.tax_rate_percent || 0).toFixed(2)}</b> {quote.tax_included ? t('detail.taxIncluded') : t('detail.taxExcluded')}</p>
            <p className="text-sm">{t('detail.globalDiscount')}: <b>%{Number(quote.global_discount_percent || 0).toFixed(2)}</b></p>
            <p className="text-sm">{t('detail.mode')}: <b>{quote.mode === 'multi' ? t('detail.modeMulti') : t('detail.modeSingle')}</b></p>
          </div>
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-4">
            <p className="text-xs text-[#94A3B8]">{t('detail.createdAt')}</p>
            <p className="font-semibold text-[#1A1A2E]">
              {quote.created_at ? new Date(quote.created_at).toLocaleString('tr-TR') : '—'}
            </p>
            {quote.valid_until && <><p className="text-xs text-[#94A3B8] mt-2">{t('detail.validUntil')}</p><p className="text-sm">{quote.valid_until}</p></>}
          </div>
        </div>

        {missingPriceCount > 0 && (
          <div className="mb-4 flex items-start gap-3 rounded-xl border border-amber-300 bg-amber-50 px-4 py-3 text-amber-900">
            <AlertTriangle className="mt-0.5 h-5 w-5 shrink-0" />
            <div>
              <p className="font-semibold">{t('detail.missingPriceTitle', { count: missingPriceCount })}</p>
              <p className="text-sm">{t('detail.missingPriceDescription')}</p>
            </div>
          </div>
        )}

        {ordered.map((pkg) => (
          <PackageBlock key={pkg.key + pkg.name} pkg={pkg} currency={quote.currency} taxLabel={quote.tax_label} t={t} showCosts={showCosts} />
        ))}

        {(quote.notes || quote.terms) && (
          <div className="bg-white rounded-2xl border border-[#E5E5E5] p-6 space-y-3">
            {quote.notes && (<>
              <h3 className="font-semibold text-[#1A1A2E]">{t('detail.notes')}</h3>
              <p className="text-[#475569] whitespace-pre-wrap text-sm">{quote.notes}</p>
            </>)}
            {quote.terms && (<>
              <h3 className="font-semibold text-[#1A1A2E]">{t('detail.terms')}</h3>
              <p className="text-[#475569] whitespace-pre-wrap text-sm">{quote.terms}</p>
            </>)}
          </div>
        )}
      </div>
    </QuoteLayout>
  );
}

export default QuoteDetail;
